import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import v4 from 'uuid/v4';
import {
  noteNerobottiDoorRefusal,
  setBankPhotoFilter,
} from 'scenes/Nerobotti/store/nerobottiSlice';
import { setBankSearchText } from 'scenes/ProjectBank/store/actions/project-bank.actions';
import {
  selectBankAiSearch,
  selectBankAiSearchTarget,
} from 'scenes/ProjectBank/store/selectors/project-bank.selectors';
import { postNerobottiPhotoSearch } from 'scenes/ProjectBank/services/postNerobottiPhotoSearch.service';
import { normalizeSearchText } from 'scenes/ProjectBank/utils/bankItemFilter';

/** Fewer characters than this is nothing to ask about; the API refuses it too. */
export const MIN_AI_SEARCH_CHARS = 2;

/**
 * The search box asking the AI assistant for photos by what they show.
 *
 * Runs only on the user's say-so — the sparkle, or Enter on a search that
 * matched nothing here — never as they type: every call spends their
 * company's allowance (see the API's `photo-search.controller`). The result
 * is the same photo filter the assistant's panel puts on the bank, so it
 * becomes the chip at the head of the filter row, named in the user's own
 * words, and the search text is cleared: the box's words and the chip would
 * otherwise both filter, and nothing matches a word *and* a set of photos
 * named by it. Nothing is added to the assistant's conversation.
 *
 * Nothing found, and a failure, are said in a snackbar rather than in the
 * list: the list shows whatever the box matched locally, which may be
 * something. A refusal at the door is also recorded as the panel's access
 * state, so the sparkle shuts the way the panel's field does.
 * @param {{query: string}} arg
 */
export const searchBankPhotosWithAi = createAsyncThunk(
  'projectBank/searchPhotosWithAi',
  async ({ query }, { dispatch, getState, rejectWithValue }) => {
    const target = selectBankAiSearchTarget(getState());
    if (!target) {
      return rejectWithValue({ query, reason: 'no_target' });
    }

    let result;
    try {
      result = await postNerobottiPhotoSearch({
        projectId: target.projectId,
        query,
        folderId: target.folderId,
      });
    } catch (error) {
      const response = error && error.response;
      const code = response && response.data && response.data.code;
      if (typeof code === 'string' && code.startsWith('nerobotti_')) {
        dispatch(
          noteNerobottiDoorRefusal({
            code,
            usedPercent: response.data.usedPercent,
          })
        );
      }
      enqueueSnackbar(
        (response && response.data && response.data.message) ||
          i18n.t('bank.ai_search_failed'),
        { variant: 'error' }
      );
      return rejectWithValue({ query, code: code || null });
    }

    if (result && result.view) {
      dispatch(setBankPhotoFilter({ ...result.view, id: v4() }));
      dispatch(setBankSearchText(''));
      return { query, status: 'done', total: result.total };
    }

    // A photo the tagger has not analysed matches nothing, so on a project
    // whose archive is still untagged a miss says so: the answer is a
    // backfill, not other words.
    const unanalysed =
      result && Number.isFinite(result.photosSearched)
        ? Math.max(0, result.photosSearched - (result.analysedPhotos || 0))
        : 0;
    enqueueSnackbar(
      unanalysed > 0
        ? i18n.t('bank.ai_search_empty_unanalysed', {
            query,
            count: unanalysed,
          })
        : i18n.t('bank.ai_search_empty', { query }),
      { variant: 'info' }
    );
    return { query, status: 'empty', total: 0 };
  },
  {
    condition: ({ query } = {}, { getState }) => {
      if (normalizeSearchText(query).length < MIN_AI_SEARCH_CHARS) return false;
      // One at a time: a second press while the first is out would put two
      // filters on the bank in whichever order the answers came.
      return selectBankAiSearch(getState()).status !== 'loading';
    },
  }
);

export default searchBankPhotosWithAi;
